import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LocationContext = createContext(null);

const ALL = "all";

function storageKey(userId) {
  return `dtp_selected_lot_${userId}`;
}

export function LocationProvider({ children }) {
  const { user } = useAuth();
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLotState] = useState(ALL);
  const [loaded, setLoaded] = useState(false);

  // Load lots once the user is authenticated (back-office only; drivers have no lot scope).
  useEffect(() => {
    if (!user || user === false || user.role === "driver") {
      setLots([]);
      setLoaded(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/lots");
        setLots(data);
        // Restore last selection, falling back to a sensible default per role.
        const saved = localStorage.getItem(storageKey(user.id));
        const validIds = data.map((l) => l.id);
        if (saved && (saved === ALL || validIds.includes(saved))) {
          setSelectedLotState(saved);
        } else if (user.role === "owner" || user.role === "manager") {
          setSelectedLotState(ALL);
        } else {
          setSelectedLotState(data.length ? data[0].id : ALL);
        }
      } finally {
        setLoaded(true);
      }
    })();
  }, [user]);

  const setSelectedLot = useCallback(
    (value) => {
      setSelectedLotState(value);
      if (user?.id) localStorage.setItem(storageKey(user.id), value);
    },
    [user]
  );

  const currentLot =
    selectedLot === ALL ? null : lots.find((l) => l.id === selectedLot) || null;

  return (
    <LocationContext.Provider
      value={{ lots, selectedLot, setSelectedLot, currentLot, isAll: selectedLot === ALL, loaded, ALL }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export const useLocationScope = () => useContext(LocationContext);
